// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {API_URL, API_USER_CMD} from 'src/shared/api';
import {catchError, map, tap} from 'rxjs/operators';
import {
    IUser,
    ICreateUser,
    IGenerate2FAResponse,
    INewRecoveryCodeResponse,
    IUsersSubscriptions,
    IUserSubscriptions,
    IUserSettings,
    UsersStats,
    IInvitationResponse,
    ITOTPRequest,
    ITOTPResponse,
    SearchUsersResponse,
    IADConfig,
    IADResponse,
    LoginResponse,
    ResendVCodeResponse,
    IManageUser,
    IEmailExists, IEditOrgUser, ICreateUserResponse, IResponse, DeactivateUsersResponse, IDisable2FAResponse, IOktaDomainResponse
} from 'src/types/user';

import {
    ISubscriptionRequest,
    ISubscriptionResult
} from 'src/types/subscription';
import {IGroup} from 'src/types/group';
import {ConfigurationService} from './configuration-service';
import {IOrgTreeResponse} from '../types/org-tree';

export enum AzureActivateStatus {
    SUCCESS = 0,
    NOT_ACCEPTABLE = 1,
    TENANT_ID = 2,
}

export enum PasswordResetStatus {
    SUCCESS = 0,
    FAILURE = 1,
    INVALID = 2,
    RECENT_PASSWORD_REUSED = 3,
    WRONG_DOMAIN = 4,
    RESETS_THROTTLED = 5,
    ACCOUNT_DEACTIVATED = 6,
    INVALID_RESET_CODE = 7,
}

export interface IPasswordResetResult {
    status: PasswordResetStatus;
}

export interface IReservedDomainsResponse {
    domains: string[];
}

@Injectable()
export class UserService {
    private _user: BehaviorSubject<IUser>;
    public orgsUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private http: HttpClient,
        private translate: TranslateService,
        private configurationService: ConfigurationService,
    ) {
        this._user = <BehaviorSubject<IUser>>new BehaviorSubject(null);
    }

    getUrl(suffix: string) {
        return `${this.configurationService.apiUrl}${suffix}`;
    }

    // GET Requests
    api_get_user_info(): Observable<IManageUser> {
        const url = this.getUrl(API_URL.API_USER);
        return this.http.get<IUser>(url).pipe(
          tap(user => {
              this._user.next(user);
          }),
          catchError(this.handleError.bind(this))
        );
    }

    api_get_user_by_id(user_id: number): Observable<IManageUser> {
        const url = this.getUrl(`${API_URL.API_USER_BY_ID}/${user_id}`);
        return this.http.get<IUser>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_user_by_invitation(invitationUUID: string): Observable<IInvitationResponse> {
        const url = this.getUrl(`${API_URL.API_USER_BY_INVITATION}/${invitationUUID}`);
        return this.http.get<IUser>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_users_stats(): Observable<UsersStats> {
        const url = this.getUrl(API_URL.API_USERS_STATS);
        return this.http.get<UsersStats>(url)
                .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_users(): Observable<IUser[]> {
        const url = this.getUrl(API_URL.API_USERS);
        return this.http.get<IUser[]>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_organizations(): Observable<IGroup[]> {
        const url = this.getUrl(API_URL.ORGANIZATIONS);
        return this.http.get<IGroup[]>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_reserved_domains(selectedOrgId: string): Observable<string[]> {
        let url = this.getUrl(API_URL.RESERVED_DOMAINS);
        if (!!selectedOrgId) {
          url = `${url}?selectedOrgId=${selectedOrgId}`
        }
        return this.http.get<IReservedDomainsResponse>(url).pipe(
          map(response => response.domains)
        );
    }

    api_search_users(searchTerm: string, selectedOrgId: string): Observable<SearchUsersResponse> {
        searchTerm = encodeURIComponent(searchTerm);
        const url = this.getUrl(`${API_URL.API_USERS_SEARCH}?term=${searchTerm}&selectedOrgId=${selectedOrgId}`);
        return this.http.get<SearchUsersResponse>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_search_users_infinite_scroll(query, selectedOrgId: string): Observable<SearchUsersResponse> {
        const url = this.getUrl(`${API_URL.API_USERS_SEARCH}?selectedOrgId=${selectedOrgId}`);
        return this.http.post<IUsersSubscriptions>(url, query)
            .pipe(catchError(this.handleError.bind(this))
        );
    }

    api_get_all_users(): Observable<IUser[]> {
        const url = this.getUrl(API_URL.API_ALL_USERS);
        return this.http.get<IUser[]>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_org_tree(): Observable<IOrgTreeResponse> {
        const url = this.getUrl(API_URL.API_ORG_TREE);
        return this.http.get<IOrgTreeResponse>(url)
          .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_super_users(): Observable<IUser[]> {
        const url = this.getUrl(API_URL.API_SUPER_USERS);
        return this.http.get<IUser[]>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_groups(): Observable<IGroup[]> {
        const url = this.getUrl(API_URL.API_GROUPS);
        return this.http.get<IGroup[]>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_parent_groups(): Observable<IGroup[]> {
        const url = this.getUrl(API_URL.API_PARENT_GROUPS);
        return this.http.get<IGroup[]>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_set_parent_group(group_id: number, new_parent_group: number): Observable<string> {
        const url = this.getUrl(API_URL.API_REPARENT_GROUP);
        const data = { group_id, new_parent_group };
        return this.http.put(url, data).pipe(catchError(this.handleError.bind(this)));
    }

    api_get_user_roles(user_id: number): Observable<number[]> {
        const url = this.getUrl(`${API_URL.API_USER_ROLES}/${user_id}`);
        return this.http.get<number[]>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_put_user_roles(user_id: number, groups: number[]): Observable<void> {
        const url = this.getUrl(`${API_URL.API_USER_ROLES}/${user_id}`);
        return this.http.put<void>(url, {groups})
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_get_users_with_subscriptions(searchTerm: string): Observable<IUserSubscriptions[]> {
        if (searchTerm && searchTerm.trim().length > 0) {
            const url = this.getUrl(API_URL.API_USERS_SEARCH_SUBS);
            const data = { 'searchTerm': searchTerm };
            return this.http.post<IUsersSubscriptions>(url, data)
                        .pipe(
                            map(result => result.users),
                            catchError(this.handleError.bind(this)
                    ));
        } else {
            const url = this.getUrl(API_URL.API_USERS_SUBSCRIPTIONS);
            return this.http.get<IUsersSubscriptions>(url)
                        .pipe(
                            map(result => result.users),
                            catchError(this.handleError.bind(this)
                    ));
        }

    }

    createSubscriptionAndUser(subscriptionRequest: ISubscriptionRequest): Observable<ISubscriptionResult> {
        const url = this.getUrl(API_URL.SUBSCRIBE);
        return this.http.post<ISubscriptionResult>(url, subscriptionRequest)
            .pipe(catchError((error: HttpErrorResponse) => {
                if (error && error.status !== HttpStatusCode.PreconditionFailed) {
                    this.handleError.bind(this);
                }
                return throwError(error);
            }));
    }

    // Settings-related calls
    api_get_settings(): Observable<IUserSettings> {
        const url = this.getUrl(API_URL.API_USER_SETTINGS);
        return this.http.get<IUserSettings>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_put_settings(settings: IUserSettings) {
        const url = this.getUrl(API_URL.API_USER_SETTINGS);
        return this.http.put<IUserSettings>(url, settings)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_edit_org(user: IEditOrgUser, selectedOrgId: string = null): Observable<IUser> {
        let url = this.getUrl(API_URL.API_USER);
        if (selectedOrgId) {
            url += `?selectedOrgId=${selectedOrgId}`;
        }
        return this.http.put<IUser>(url, user)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_put_user(user: IUser, selectedOrgId: string = null): Observable<IUser> {
        let url = this.getUrl(API_URL.API_USER);
        if (selectedOrgId) {
            url += `?selectedOrgId=${selectedOrgId}`;
        }
        return this.http.put<IUser>(url, user)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_users_deactivate(user_ids: number[], selectedOrgId: string = null): Observable<DeactivateUsersResponse> {
        let url = this.getUrl(API_URL.API_USERS_DEACTIVATE);
        if (selectedOrgId) {
            url += `?selectedOrgId=${selectedOrgId}`;
        }
        return this.http.post<DeactivateUsersResponse>(url, {user_ids})
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_users_reactivate(user_ids: number[], selectedOrgId: string = null): Observable<string> {
        let url = this.getUrl(API_URL.API_USERS_REACTIVATE);
        if (selectedOrgId) {
            url += `?selectedOrgId=${selectedOrgId}`;
        }
        return this.http.post<string>(url, {user_ids})
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_users_force_expire_passwords(user_ids: number[], selectedOrgId: string = null): Observable<void> {
        let url = this.getUrl(API_URL.API_USERS_FORCE_EXPIRE);
        if (selectedOrgId) {
            url += `?selectedOrgId=${selectedOrgId}`;
        }
        return this.http.post<string>(url, {user_ids})
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_post_user(user: ICreateUser, selectedOrgId: string = null): Observable<IUser> {
        let url = this.getUrl(API_URL.API_USER);
        if (selectedOrgId) {
            url += `?selectedOrgId=${selectedOrgId}`;
        }
        return this.http.post<IUser>(url, user)
            .pipe(catchError(this.handleError.bind(this)));
    }

    api_post_organization(user: ICreateUser): Observable<IUser> {
        const url = this.getUrl( `${API_URL.API_USER}?isOrganization=true`);
        return this.http.post<IUser>(url, user)
            .pipe(catchError(this.handleError.bind(this)));
    }

    deactivate_organization(organizations): Observable<string> {
        const url = this.getUrl(API_URL.DEACTIVATE_ORGANIZATION);
        const body = { 'organizations': organizations };
        return this.http.post<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    reactivate_organization(organizations): Observable<string> {
        const url = this.getUrl(API_URL.REACTIVATE_ORGANIZATION);
        const body = { 'organizations': organizations };
        return this.http.post<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    get_organization(organizationId): Observable<IGroup> {
        if (organizationId == null) {
            return EMPTY;
        }
        const url = this.getUrl(`${API_URL.API_ORGANIZATION}/${organizationId}`);
        return this.http.get<IResponse>(url).pipe(catchError(this.handleError.bind(this)));
    }

    delete_organization(organizationId): Observable<IResponse> {
        const url = this.getUrl(`${API_URL.API_ORGANIZATION}/${organizationId}`);
        return this.http.delete<IResponse>(url).pipe(catchError(this.handleError.bind(this)));
    }

    send_delete_organization_request(selectedOrgId: string = null): Observable<string> {
        const url = this.getUrl(`${API_URL.API_ORGANIZATION}/${selectedOrgId}/delete-request`);
        const body = {};
        return this.http.post<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getUser(selectedOrgId = '') {
        let url = this.getUrl(API_URL.API_USER);
        if ( selectedOrgId.length > 0 ) {
            url += '?selectedOrgId=' + selectedOrgId;
        }
        return this.http.get<IUser>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    subscribeToOrgsChanges(): Observable<boolean> {
        return this.orgsUpdated$.asObservable();
    }

    refreshOrgs() {
        this.orgsUpdated$.next(true);
    }

    subscribeToUser(): Observable<IUser> {
        return this._user.asObservable();
    }

    refreshUser() {
        const url = this.getUrl(API_URL.API_USER);
        this.http.get<IUser>(url)
            .pipe(catchError(this.handleError.bind(this)))
            .subscribe(
                user => this._user.next(user),
                error => this.handleError(error),
            );
    }

    // USER 'commands'
    checkUserEmailExists(email: string): Observable<IEmailExists> {
        const url = this.getUrl(API_USER_CMD.EMAIL_EXISTS);
        const emailRequest = { email };
        return this.http.post(url, emailRequest)
            .pipe(catchError(this.handleError.bind(this)));
    }

    checkUserEmailExistsForUser(user_id: number, email: string): Observable<IEmailExists> {
        const url = this.getUrl(API_USER_CMD.EMAIL_EXISTS_FOR_USER);
        const emailRequest = { user_id, email };
        return this.http.post(url, emailRequest)
            .pipe(catchError(this.handleError.bind(this)));
    }

    checkUsernameExists(user_id: number, username: string): Observable<{ exists: boolean; }> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.USERNAME_EXISTS);
        const request = { user_id, username };
        return this.http.post<string>(url, request)
            .pipe(catchError(this.handleError.bind(this)));
    }

    createUser(verificationRequest: {}): Observable<ICreateUserResponse> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.CREATE_USER);
        return this.http.post<ICreateUserResponse>(url, verificationRequest)
            .pipe(catchError(this.handleError.bind(this)));
    }

    deleteUser(userId: number): Observable<string> {
        const url = this.getUrl(`${API_URL.API_USER}/${userId}`);
        return this.http.delete<string>(url)
          .pipe(catchError(this.handleError.bind(this)));
    }

    resendVerificationEmail(email: string): Observable<HttpResponse<ResendVCodeResponse>> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.RESEND_VERIFY_EMAIL);
        const resendRequest = { email };
        return this.http.put<ResendVCodeResponse>(url, resendRequest, {observe: 'response'});
    }

    sendVerificationToNewEmail(new_email: string): Observable<void> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.SEND_VERIFY_NEW_EMAIL);
        const resendRequest = { new_email };
        return this.http.put(url, resendRequest)
            .pipe(catchError(this.handleError.bind(this)));
    }

    checkVerificationCode(email: string, code: string, stripePlanID: string): Observable<HttpResponse<Object>> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.VERIFY_EMAIL);
        const verifyRequest = { email, code, stripePlanID };
        return this.http.put(url, verifyRequest, {observe: 'response'});
    }

    checkVerificationCodeForNewEmail(email: string, code: number): Observable<void> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.VERIFY_NEW_EMAIL);
        const verifyRequest = { email, code };
        return this.http.put(url, verifyRequest)
            .pipe(catchError(this.handleError.bind(this)));
    }

    updateUser(user: IUser): Observable<string> {
        const url = this.getUrl(API_URL.API_USER);
        return this.http.put<string>(url, user)
            .pipe(catchError(this.handleError.bind(this)));
    }

    reParentUser(user_id: number, new_group_id: number): Observable<any> {
        const url = this.getUrl(API_URL.API_USERS_REPARENT);
        const json_body = {
            'user_id': user_id,
            'new_group_id': new_group_id
        };
        return this.http.post<string>(url, json_body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    updatePassword(oldPass: string, newPass: string): Observable<IPasswordResetResult> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.UPDATEPASSWORD);
        const body = {
            'oldpass': oldPass,
            'newpass': newPass
        };
        return this.http.put<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    changePasswordByUsername(username: string, oldPass: string, newPass: string): Observable<IPasswordResetResult> {
        const url = this.getUrl(API_USER_CMD.API_V1_PASSWORD);
        const body = {
            username: username,
            oldpass: oldPass,
            newpass: newPass
        };
        return this.http.put<IPasswordResetResult>(url, body).pipe(catchError(this.handleError.bind(this)));
    }

    sendPasswordResetEmail(email: string, partnerId: number): Observable<IPasswordResetResult> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.RESETPASSWORD);
        const body = { 'email': email, 'partner_id': partnerId };
        return this.http.post<IPasswordResetResult>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    resetPasswordVerification(email: string, code: string): Observable<string> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.VERIFYCODE);
        const body = { 'email': email, 'code': code };
        return this.http.post<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    resetPasswordByInvitation(email: string, invitationUUID: string, password: string): Observable<string> {
        const url = this.getUrl(API_URL.API_INVITATION_RESET);
        const body = { email, invitationUUID, password };
        return this.http.post<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    resetPassword(email: string, code: string, password: string): Observable<IPasswordResetResult> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.RESETPASSWORD);
        const body = { 'email': email, 'code': code, 'password': password };
        return this.http.put<IPasswordResetResult>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getTwoFactor(): Observable<IGenerate2FAResponse> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.GET_2FA);
        return this.http.put(url, '')
            .pipe(catchError(this.handleError.bind(this)));
    }

    addVerifyTotp(totp: number): Observable<ITOTPResponse> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.ADD_VERIFY_TOTP);
        const body: ITOTPRequest = {'totpCode': totp};
        return this.http.post<ITOTPResponse>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getNewRecoveryCode(password: string): Observable<INewRecoveryCodeResponse> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.NEW_RECOVERY_CODE);
        return this.http.post(url, {password})
            .pipe(catchError(this.handleError.bind(this)));
    }

    disable2FA(password: string): Observable<IDisable2FAResponse> {
        const url = this.getUrl(API_URL.API_USER + API_USER_CMD.GET_2FA);
        return this.http.post<IDisable2FAResponse>(url, {password})
            .pipe(catchError(this.handleError.bind(this)));
    }

    userLogin(username: string, password: string, second_factor:
              string, activationToken: string, deviceToken: string, stayLoggedIn: boolean,
              partnerId = 0): Observable<LoginResponse> {
        let url = this.getUrl(API_USER_CMD.API_V1_LOGIN);
        if (deviceToken) {
            const stayLoggedInStr = stayLoggedIn ? 'true' : 'false';
            url = `${url}?device=${deviceToken}&stayLoggedIn=${stayLoggedInStr}`;
        }
        const body = {
            'username': username,
            'password': password,
            'second_factor': second_factor,
            'activation_token': activationToken,
            'partner_id': partnerId
        };
        return this.http.post<LoginResponse>(url, body).pipe(catchError(this.handleErrorResponse.bind(this)));
    }

    getOktaDomain(domain: string): Observable<IOktaDomainResponse> {
        const url = this.getUrl(`${API_USER_CMD.API_V1_OKTA_DOMAIN}?domain=${domain}`);
        return this.http.get<IOktaDomainResponse>(url);
    }

    oktaUserLogin(tokens: any, deviceToken: string, stayLoggedIn: boolean): Observable<LoginResponse> {
        let url = this.getUrl(API_USER_CMD.API_V1_OKTA_LOGIN);
        if (deviceToken) {
            const stayLoggedInStr = stayLoggedIn ? 'true' : 'false';
            url = `${url}?device=${deviceToken}&stayLoggedIn=${stayLoggedInStr}`;
        }
        return this.http.post<LoginResponse>(url, tokens);
    }

    renewToken(): Observable<LoginResponse> {
        const url = this.getUrl(API_URL.USER + API_USER_CMD.RENEW_TOKEN);
        return this.http.get<string>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getADConfig(): Observable<IADResponse> {
        const url = this.getUrl(`${API_URL.ACTIVE_DIRECTORY_CONFIG}`);
        return this.http.get<IADConfig>(url)
            .pipe(catchError(this.handleError.bind(this)));
    }

    loginToAD(idToken: string, deviceToken: string, stayLoggedIn: boolean, partnerId): Observable<LoginResponse> {
        let url = this.getUrl(API_URL.ACTIVE_DIRECTORY_LOGIN);
        if (deviceToken) {
            const stayLoggedInStr = stayLoggedIn ? 'true' : 'false';
            url = `${url}?device=${deviceToken}&stayLoggedIn=${stayLoggedInStr}`;
        }
        const body = {'id_token': idToken,
                      'partner_id': partnerId};
        return this.http.post<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    azureMarketActivate(activationToken: string, idToken: string): Observable<{ message: string; status: AzureActivateStatus}> {
        const url = this.getUrl( `${API_URL.AZURE_MARKET_ACTIVATE}`);
        const body = {'token': activationToken,
                      'id_token': idToken};
        return this.http.post<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    acceptCigentPolicies(token: string): Observable<string> {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        const url = this.getUrl(API_USER_CMD.POLICY_ACCEPTANCE);
        const body = {};
        return this.http.put<string>(url, body, { headers: headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    dismissTour(): Observable<string> {
        const url = this.getUrl(API_USER_CMD.API_DISMISS_TOUR);
        const body = {};
        return this.http.put<string>(url, body)
            .pipe(catchError(this.handleError.bind(this)));
    }

    handleErrorResponse(errResponse: HttpErrorResponse | any): any {
        return throwError(errResponse.error || 'Server error');
    }

    handleError(errResponse: HttpErrorResponse | any): any {
        if ((errResponse instanceof HttpErrorResponse) && errResponse.status) {
            return new Promise((resolve, reject) => {
                this.translate.get(errResponse.statusText).subscribe(
                    (translated) => reject(translated),
                    () => reject('Server Error')
                );
            });
        }
        return throwError(errResponse.error || 'Server error');
    }

    getExportUserJsonUrl(token: string) {
        return this.getUrl(`${API_USER_CMD.API_EXPORT_USERS_JSON}?q=${token}`);
    }

    getExportUserCsvUrl(token: string) {
        return this.getUrl(`${API_USER_CMD.API_EXPORT_USERS_CSV}?q=${token}`);
    }

}
